import { isBooleanTrue, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from '../utils/objectUtils';
import { getClientIPAddress, getClientUserAgent, getReferrerUrl } from '../utils/clientUtils';
import { getCookie, getDomain, setCookie } from '../utils/cookieUtils';
import { toLower } from 'lodash';
import SiteVisit from '../models/SiteVisit';
import SiteEvent from '../models/SiteEvent';
import AffiliateInfo from '../models/AffiliateInfo';
import DeviceFingerprint from 'node_js_ipqs_device_tracker';
import { createTheOfferSession } from '../firebase/TheOffer';
import { sendImpactPageLoad } from '../firebase/Impact';
import { setAffiliateInfo } from '../redux/ducks/analytics';
import logger from '../utils/logger';
import { getTestModeConsole, setTestModeConsole } from '../utils/testMode';

// Users will be considered part of the same session for 30 days
// Analytics will group user activity within these 30-day sessions
// Impressions will be tracked once per 30-day period
// The visitor ID will still persist for a year for long-term user recognition

const IP_QUALITY_SCORE_FINGERPRINT_TRACKER_KEY = process.env.REACT_APP_IP_QUALITY_SCORE_FINGERPRINT_TRACKER_KEY;
class TrackingService {
    static COOKIE_PREFIX = 'rrx_';
    static VISIT_ID_COOKIE = `${TrackingService.COOKIE_PREFIX}visit_id`; // 30 day expiration
    static VISITOR_ID_COOKIE = `${TrackingService.COOKIE_PREFIX}visitor_id`; // 1 year expiration
    static FBC_COOKIE = `${TrackingService.COOKIE_PREFIX}fbc`; // 90 day expiration
    static VISIT_DATA_KEY = 'rrx_visit_data'; // localStorage key

    // same endpoints public/readyrx.js uses; only defaulted for production builds so a future
    // dev/staging project without overrides degrades to SDK-only writes instead of writing to prod
    static PROD_FIREBASE_PROJECT_ID = 'ready-rx-cd342';
    static SITE_VISIT_API_URL = isStringNotEmpty(process.env.REACT_APP_SITE_VISIT_API_URL)
        ? process.env.REACT_APP_SITE_VISIT_API_URL
        : (process.env.REACT_APP_FIREBASE_PROJECT_ID === TrackingService.PROD_FIREBASE_PROJECT_ID ? 'https://createsitevisitapi-vxwmzv7rgq-uc.a.run.app' : null);
    static SITE_EVENT_API_URL = isStringNotEmpty(process.env.REACT_APP_SITE_EVENT_API_URL)
        ? process.env.REACT_APP_SITE_EVENT_API_URL
        : (process.env.REACT_APP_FIREBASE_PROJECT_ID === TrackingService.PROD_FIREBASE_PROJECT_ID ? 'https://createsiteeventapi-vxwmzv7rgq-uc.a.run.app' : null);
    static SITE_VISIT_UPDATE_API_URL = isStringNotEmpty(process.env.REACT_APP_SITE_VISIT_UPDATE_API_URL)
        ? process.env.REACT_APP_SITE_VISIT_UPDATE_API_URL
        : (process.env.REACT_APP_FIREBASE_PROJECT_ID === TrackingService.PROD_FIREBASE_PROJECT_ID ? 'https://updatesitevisitapi-vxwmzv7rgq-uc.a.run.app' : null);
    static EVERFLOW_CLICK_API_URL = isStringNotEmpty(process.env.REACT_APP_EVERFLOW_CLICK_API_URL)
        ? process.env.REACT_APP_EVERFLOW_CLICK_API_URL
        : (process.env.REACT_APP_FIREBASE_PROJECT_ID === TrackingService.PROD_FIREBASE_PROJECT_ID ? 'https://createeverflowclickapi-vxwmzv7rgq-uc.a.run.app' : null);

    // the quarterly default promo applied when no ?coupon= is present. rotated each quarter - a single
    // named source of truth so the seed-when-empty check can treat a stored default as unseeded.
    // keep in sync with the functions-side DEFAULT_COUPON_CODE in tracking.js
    static DEFAULT_COUPON_CODE = 'gethealthy26';

    static initialized = false;
    static initializing = false;
    static initFailed = false;

    static pendingFunnelEvents = [];
    static funnelEventFlushTimer = null;
    static unloadFlushRegistered = false;
    // events that never resolve a visit can still be lost on a true unload inside this window
    static FUNNEL_EVENT_FLUSH_TIMEOUT_MS = 8000;

    // a firestore write neither resolves nor rejects while offline - every await on the
    // initialize() path stays bounded or the flush guards park the queue for the session
    static INIT_AWAIT_TIMEOUT_MS = 8000;

    // set when a re-attribution visit fails to create: the stored visit then carries the
    // previous campaign's attribution and must not be merged into this session's events
    static isStoredVisitStale = false;

    static async boundInitAwait(promise) {
        // a late rejection after the timeout wins would otherwise surface as an unhandled
        // rejection; every caller already treats null as "didn't happen"
        const guarded = Promise.resolve(promise).catch((error) => {
            logger.logErrorEvent('TRACKING_BOUNDED_OP', error, 'Bounded tracking operation failed', getTestModeConsole());
            return null;
        });

        return await Promise.race([
            guarded,
            new Promise((resolve) => setTimeout(() => resolve(null), TrackingService.INIT_AWAIT_TIMEOUT_MS))
        ]);
    }

    // the funnel can render (and fire events) before initialize() creates the siteVisit; events
    // queue with occurredAt stamped at capture time and flush with the visit's attribution merged
    static queueFunnelEvent(eventFields) {
        this.pendingFunnelEvents.push(eventFields);

        if (this.funnelEventFlushTimer === null) {
            this.funnelEventFlushTimer = setTimeout(() => {
                this.funnelEventFlushTimer = null;
                this.flushFunnelEventQueue();
            }, TrackingService.FUNNEL_EVENT_FLUSH_TIMEOUT_MS);
        }

        if (!this.unloadFlushRegistered && typeof window !== 'undefined') {
            this.unloadFlushRegistered = true;
            const flushBeforeUnload = () => { this.flushFunnelEventQueueOnUnload(); };
            window.addEventListener('pagehide', flushBeforeUnload);
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') { flushBeforeUnload(); }
            });
        }
    }

    // session-stable fallback id for browsers where even the visitor cookie is unavailable
    static fallbackAnonymousId = null;

    // merges the resolved visit's attribution into a queued event and rebases pre-visit
    // sessionKeys so one user's funnel doesn't split into multiple funnelSessions server-side
    static mergeQueuedEvent(eventFields, resolvedVisit) {
        const merged = {
            ...(resolvedVisit ?? {}),
            ...eventFields,
            siteVisitId: isStringNotEmpty(eventFields?.siteVisitId) ? eventFields.siteVisitId : (resolvedVisit?.id ?? null)
        };

        if (typeof merged.sessionKey === 'string' && isStringNotEmpty(resolvedVisit?.id)) {
            if (merged.sessionKey.startsWith('anonymous_')) {
                merged.sessionKey = merged.sessionKey.replace(/^anonymous_/, `${resolvedVisit.id}_`);
            } else if (isStringNotEmpty(merged.leadId) && merged.sessionKey.startsWith(`${merged.leadId}_`)) {
                // auto-logged-in ?lid= returns key pre-visit events by leadId
                merged.sessionKey = `${resolvedVisit.id}_` + merged.sessionKey.slice(`${merged.leadId}_`.length);
            }
        } else if (typeof merged.sessionKey === 'string') {
            // no visit resolved: rebase every pre-visit base onto the visitor id - anonymous
            // keys pool users into one shared session, untouched leadId keys split a user's run
            let visitorId = getCookie(TrackingService.VISITOR_ID_COOKIE);
            if (isStringEmpty(visitorId)) {
                if (this.fallbackAnonymousId === null) {
                    this.fallbackAnonymousId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') ? crypto.randomUUID() : `anon-${Date.now()}`;
                }
                visitorId = this.fallbackAnonymousId;
            }
            if (merged.sessionKey.startsWith('anonymous_')) {
                merged.sessionKey = merged.sessionKey.replace(/^anonymous_/, `${visitorId}_`);
            } else if (isStringNotEmpty(merged.leadId) && merged.sessionKey.startsWith(`${merged.leadId}_`)) {
                merged.sessionKey = `${visitorId}_` + merged.sessionKey.slice(`${merged.leadId}_`.length);
            }
        }

        return merged;
    }

    static async flushFunnelEventQueue(siteVisit = null) {
        if (this.pendingFunnelEvents.length === 0) { return; }

        // draining mid-init would write events unattributed or against a stale previous visit;
        // defer to initialize()'s own flush, which runs after the flag resets
        if (this.initializing === true) {
            return;
        }

        if (this.funnelEventFlushTimer !== null) {
            clearTimeout(this.funnelEventFlushTimer);
            this.funnelEventFlushTimer = null;
        }

        let resolvedVisit = siteVisit;
        // the stored visit is only a safe fallback while it still reflects this session's
        // attribution - after a failed re-attribution it belongs to the previous campaign
        if (isObjectEmpty(resolvedVisit) && this.isStoredVisitStale !== true) {
            resolvedVisit = await this.getStoredVisitData();
        }

        // re-check: initialize() could have started while the stored-visit read was in flight
        if (this.initializing === true) {
            return;
        }

        // independent writes with occurredAt pre-stamped at capture time - flush concurrently
        // so a slow link doesn't hold the queue open one round-trip per event
        const events = this.pendingFunnelEvents.splice(0, this.pendingFunnelEvents.length);
        const results = await Promise.allSettled(
            events.map((eventFields) => new SiteEvent(this.mergeQueuedEvent(eventFields, resolvedVisit)).create())
        );
        results.forEach((result) => {
            if (result.status === 'rejected') {
                logger.logErrorEvent('TRACKING_FUNNEL_FLUSH', result.reason, 'Error flushing queued funnel event', getTestModeConsole());
            }
        });
    }

    /*
        Best-effort drain when the page may be going away: keepalive POSTs survive unload where
        the Firestore SDK does not. visibilitychange also fires on ordinary tab switches, so
        events are never written without attribution from here - mid-init the whole queue stays
        put for initialize()'s flush, and events with no visit id are re-queued for the still-
        alive page. A true close inside those windows loses them (accepted).
    */
    static flushFunnelEventQueueOnUnload() {
        if (this.pendingFunnelEvents.length === 0) { return; }

        if (this.initializing === true) {
            return;
        }

        let resolvedVisit = null;
        if (this.isStoredVisitStale !== true) {
            try {
                const localData = localStorage.getItem(TrackingService.VISIT_DATA_KEY);
                resolvedVisit = isStringNotEmpty(localData) ? JSON.parse(localData) : null;
            } catch (e) {
                // proceed without stored visit data
            }
        }

        const events = this.pendingFunnelEvents.splice(0, this.pendingFunnelEvents.length);
        const requeue = [];
        for (const eventFields of events) {
            const merged = this.mergeQueuedEvent(eventFields, resolvedVisit);
            try {
                if (isStringNotEmpty(merged.siteVisitId)) {
                    if (isStringNotEmpty(TrackingService.SITE_EVENT_API_URL)) {
                        // text/plain avoids a CORS preflight, the round-trip most likely to be
                        // dropped mid-unload; the server parses the raw body either way
                        fetch(TrackingService.SITE_EVENT_API_URL, {
                            method: 'POST',
                            keepalive: true,
                            headers: { 'Content-Type': 'text/plain' },
                            body: JSON.stringify(merged)
                        }).catch(() => { /* best effort */ });
                    } else {
                        // no endpoint override in a non-production build
                        new SiteEvent(merged).create().catch(() => { /* best effort */ });
                    }
                } else {
                    requeue.push(eventFields);
                }
            } catch (error) {
                // never block unload on analytics
            }
        }

        if (requeue.length > 0) {
            this.pendingFunnelEvents.push(...requeue);
        }
    }

    static setDispatch(dispatch) {
        this.dispatch = dispatch;
    }

    static async initialize() {
        // Prevent multiple initializations
        if (this.initialized || this.initializing) return;

        // console.log('initializing');
        this.initializing = true;
        let siteVisit = null;
        // gate tracking console output for customers: on in dev builds and when ?test=true only
        setTestModeConsole(new URLSearchParams(window.location.search).get('test') === 'true');

        try {
            logger.logDebugEvent('TRACKING_INIT', 'Initializing TrackingService...', getTestModeConsole());
            // Initial data gathering
            const params = new URLSearchParams(window.location.search);
            // console.log('Current URL params:', params.toString());
            const pathParams = this.getPathParameters();
            // console.log('Path parameters:', pathParams);
            const initialPath = window.location.pathname;
            // full landing href, captured now because an SPA navigation during init would change
            // window.location before the landing pageview fires (query string must survive on it)
            const initialUrl = window.location.href;

            // stamped server-side by createsitevisitapi from request headers - more reliable than
            // a client-side lookup (ad blockers) and keeps a round-trip off the init path
            const ipAddress = null;
            let deviceFingerprint = null;
            try {
                logger.logDebugEvent('TRACKING_DEVICE_FINGERPRINT', 'Initializing device fingerprint...', getTestModeConsole());
                deviceFingerprint = await this.initializeDeviceFingerprint();
                logger.logDebugEvent('TRACKING_DEVICE_FINGERPRINT', `Device fingerprint initialized: ${deviceFingerprint}`, getTestModeConsole());
            } catch (err) {
                logger.logErrorEvent('TRACKING_DEVICE_FINGERPRINT', err, 'Error initializing device fingerprint', getTestModeConsole());
            }

            const trackingData = await this.gatherTrackingData(params, pathParams, {
                ipAddress,
                deviceFingerprint,
                initialPath,
                initialUrl
            });

            // update the url params without reloading the page
            // const currentUrl = new URL(window.location.href);
            // if (isStringNotEmpty(trackingData?.couponCode)) {
            //     currentUrl.searchParams.set('coupon', trackingData?.couponCode);
            //     window.history.replaceState(null, '', currentUrl.toString());
            // }

            logger.logDebugEvent('TRACKING_INIT', `Initial tracking data: ${JSON.stringify(trackingData)}`, getTestModeConsole());

            // Handle special affiliate cases. Guarded so a TheOffer failure degrades to a visit
            // without a session id instead of aborting initialization (no visit at all).
            if (trackingData.affiliateId === '2' && isStringEmpty(trackingData.theOfferSession)) {
                try {
                    const sessionId = await this.createTheOfferSession(trackingData);
                    trackingData.sessionId = sessionId;
                    trackingData.transactionId = sessionId;
                    trackingData.theOfferSession = sessionId;
                } catch (theOfferError) {
                    logger.logErrorEvent('TRACKING_THEOFFER_SESSION', theOfferError, 'TheOffer session creation failed; continuing without one', getTestModeConsole());
                }
            }

            // Everflow direct-link publishers land traffic here without passing through the tracking
            // domain's redirect, so no _ef_transaction_id is on the url and nothing has minted the
            // click yet. Guarded the same way: a failure degrades to a visit without a transaction id.
            if (trackingData.affiliateId === 'evr' && isStringEmpty(trackingData.transactionId)) {
                try {
                    const transactionId = await this.createEverflowClick(trackingData, params);
                    if (isStringNotEmpty(transactionId)) {
                        trackingData.transactionId = transactionId;
                    }
                } catch (everflowError) {
                    logger.logErrorEvent('TRACKING_EVERFLOW_CLICK', everflowError, 'Everflow click creation failed; continuing without a transaction id', getTestModeConsole());
                }
            }

            // Get or create visitor ID (long-term)
            let visitorId = getCookie(TrackingService.VISITOR_ID_COOKIE);
            if (!visitorId) {
                visitorId = crypto.randomUUID();
                setCookie(TrackingService.VISITOR_ID_COOKIE, visitorId, 365); // 1 year
            }
            logger.logDebugEvent('TRACKING_VISITOR_ID', `Visitor ID: ${visitorId}`, getTestModeConsole());

            // Get current visit data if exists
            let currentVisit = await this.getStoredVisitData();
            logger.logDebugEvent('TRACKING_INIT', `Current visit data: ${JSON.stringify(currentVisit)}`, getTestModeConsole());

            // Create affiliate info
            const affiliateInfo = await this.processAffiliateData(trackingData, currentVisit?.id);

            // Continue the cookie-resolved visit unless attribution genuinely changed. A fork mints
            // a clean new visit; a continuation reuses the existing id (this is what stops the
            // handoff from minting a second, unlinked visit) and enriches it per the field policy.
            if (isObjectNotEmpty(currentVisit)) {
                const shouldFork = this.shouldForkVisit(currentVisit, trackingData);

                if (isBooleanTrue(shouldFork)) {
                    this.dispatch(setAffiliateInfo(affiliateInfo));

                    // genuine new campaign: mint a clean new visit from the new params only. Spreading
                    // currentVisit would stamp the prior affiliate's original-visit record onto the
                    // new payable party's session
                    siteVisit = await this.createSiteVisit(trackingData);

                    if (isObjectNotEmpty(siteVisit)) {
                        await this.boundInitAwait(this.trackImpression(siteVisit));
                    } else {
                        // the stored visit now carries the previous campaign's attribution -
                        // queued events must not flush against it
                        this.isStoredVisitStale = true;
                    }
                } else {
                    siteVisit = currentVisit;
                    // persist the continued visit to this origin's localStorage so the funnel event
                    // queue flush and the unload drain (localStorage-only) resolve its id from here,
                    // rather than the cookie->server fallback that is unreliable on WebKit (ITP /
                    // in-app WebView cookie isolation) and was dropping siteVisitId off queued step
                    // events on Safari/iOS once continue became the common path. Written directly
                    // (not via storeVisitData) to avoid its getStoredVisitData round-trip on the
                    // funnel-critical init path - and, deliberately, to NOT re-set the rrx_visit_id
                    // cookie: it already exists (it's how currentVisit was resolved), and re-stamping
                    // it here would roll the 30-day anonymous-attribution cap forward on every visit.
                    try {
                        localStorage.setItem(TrackingService.VISIT_DATA_KEY, JSON.stringify(currentVisit));
                    } catch (storeError) {
                        logger.logErrorEvent('TRACKING_STORE_VISIT', storeError, 'localStorage unavailable persisting continued visit', getTestModeConsole());
                    }
                    this.enrichContinuedVisit(currentVisit, trackingData);
                }

                await this.boundInitAwait(this.trackPageView(siteVisit));
            } else {
                // No existing visit, create new one
                siteVisit = await this.createSiteVisit(trackingData);
                if (isObjectNotEmpty(siteVisit)) {
                    // handle Impact affiliate only if no existing site visit
                    if (trackingData.affiliateId === '3') {
                        await this.boundInitAwait(this.sendImpactPageLoad(siteVisit));
                    }

                    await this.boundInitAwait(this.trackPageView(siteVisit));
                    await this.boundInitAwait(this.trackImpression(siteVisit));
                }
            }

            logger.logEvent('TRACKING_SITE_VISIT', `Site visit created: ${siteVisit?.id}`, getTestModeConsole());

            // Mark as initialized at the end
            this.initialized = true;

            // the visit carries the server-stamped ip; an explicit null here would blind-spread
            // over the redux value the separate getIPAddress() lookup already set
            const returnedTrackingData = { ...trackingData };
            if (isStringNotEmpty(siteVisit?.ipAddress)) {
                returnedTrackingData.ipAddress = siteVisit.ipAddress;
            } else if (isStringEmpty(returnedTrackingData.ipAddress)) {
                delete returnedTrackingData.ipAddress;
            }

            return {
                visitorId,
                siteVisit,
                siteVisitId: siteVisit?.id,
                affiliateInfo,
                trackingData: returnedTrackingData,
                isNewVisit: !currentVisit || siteVisit?.id !== currentVisit?.id,
                deviceFingerprint: deviceFingerprint
            };
        } catch (error) {
            logger.logErrorEvent('TRACKING_INIT', error, 'Tracking initialization error', getTestModeConsole());
            // mark the failure so trackAnalyticsSiteEvent writes events through immediately
            // instead of queueing them into a flush that will never have a visit to merge
            this.initFailed = true;
            return null;
        } finally {
            // always release the flush guards (otherwise the queue is parked for the session
            // and no retry can ever run), then drain anything queued while init was in flight
            this.initializing = false;
            this.flushFunnelEventQueue(siteVisit);
        }
    }

    static async createTheOfferSession(data) {
        const { clickId, sub1, sub2, sub3, sub4, sub5 } = data;

        return await createTheOfferSession(clickId, sub1, sub2, sub3, sub4, sub5);
    }

    // everflow attributes the click from oid/affid alone; every other landing param rides along
    // verbatim - the affiliate optimizes Google/Meta bidding off params everflow itself ignores
    static async createEverflowClick(trackingData, params) {
        if (isStringEmpty(trackingData?.offerId) || isStringEmpty(trackingData?.affId)) { return null; }
        if (isStringEmpty(TrackingService.EVERFLOW_CLICK_API_URL)) { return null; }

        const abortController = new AbortController();
        const abortTimer = setTimeout(() => abortController.abort(), 5000);
        try {
            const apiResponse = await fetch(TrackingService.EVERFLOW_CLICK_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    oid: trackingData.offerId,
                    affid: trackingData.affId,
                    params: Object.fromEntries(params.entries()),
                    userAgent: trackingData.userAgent,
                    ipAddress: trackingData.ipAddress
                }),
                signal: abortController.signal
            });

            if (!apiResponse.ok) {
                logger.logErrorEvent('TRACKING_EVERFLOW_CLICK', new Error(`createeverflowclickapi responded with status ${apiResponse.status}`), 'createeverflowclickapi returned a non-OK status', getTestModeConsole());
                return null;
            }

            const res = await apiResponse.json();
            return res?.transactionId ?? null;
        } finally {
            clearTimeout(abortTimer);
        }
    }

    static async sendImpactPageLoad(siteVisit) {
        const { subAffiliateId, id, url, referrerUrl, userAgent, ipAddress } = siteVisit;

        return await sendImpactPageLoad(
            subAffiliateId,
            id,
            url,
            referrerUrl,
            userAgent,
            ipAddress
        );
    }

    static async updateVisitDataWithProfileInfo(userData = {}) {
        try {
            const currentData = await this.getStoredVisitData();
            const updatedData = {
                ...(currentData ?? {}),
                ...userData
            };

            // Store only essential ID in cookie for cross-domain tracking
            if (updatedData?.id) {
                setCookie(TrackingService.VISIT_ID_COOKIE, updatedData.id, 30);
            }
        } catch (error) {
            logger.logErrorEvent('TRACKING_STORE_VISIT', error, 'Error storing visit data', getTestModeConsole());
        }
    }

    static async storeVisitData(visitData) {
        try {
            const currentData = await this.getStoredVisitData();
            const updatedData = {
                ...(currentData ?? {}),
                ...visitData
            };

            // Store full data in localStorage
            try {
                localStorage.setItem(TrackingService.VISIT_DATA_KEY, JSON.stringify(updatedData));
            } catch (localStorageError) {
                logger.logErrorEvent('TRACKING_STORE_VISIT', localStorageError, 'localStorage not available, falling back to cookie storage', getTestModeConsole());
                // Fallback to cookie with minimal data if localStorage fails
                setCookie(TrackingService.VISIT_ID_COOKIE, updatedData.id, 30);
                return;
            }

            // Store only essential ID in cookie for cross-domain tracking
            if (updatedData?.id) {
                setCookie(TrackingService.VISIT_ID_COOKIE, updatedData.id, 30);
            }
        } catch (error) {
            logger.logErrorEvent('TRACKING_STORE_VISIT', error, 'Error storing visit data', getTestModeConsole());
        }
    }

    static async getStoredVisitData() {
        try {
            // Try localStorage first (full data)
            const localData = localStorage.getItem(TrackingService.VISIT_DATA_KEY);
            if (localData) {
                return JSON.parse(localData);
            }
        } catch (localStorageError) {
            logger.logErrorEvent('TRACKING_GET_VISIT', localStorageError, 'Error reading from localStorage', getTestModeConsole());
        }

        // Fallback to server-side data
        try {
            const visitId = getCookie(TrackingService.VISIT_ID_COOKIE);
            if (isStringNotEmpty(visitId)) {
                // bounded: this read sits on the initialize() path (and the flush paths), and an
                // offline Firestore read hangs just like a write
                const visitData = await this.boundInitAwait(SiteVisit.getById(visitId));
                if (visitData) {
                    return visitData;
                }
            }
        } catch (e) {
            logger.logErrorEvent('TRACKING_GET_VISIT', e, 'Error parsing visit cookie', getTestModeConsole());
            // Handle legacy format - just visit ID string
            const visitId = getCookie(TrackingService.VISIT_ID_COOKIE);
            if (isStringNotEmpty(visitId)) {
                return { id: visitId };
            }
        }

        return null;
    }

    // none/empty both mean "no attribution" - the affiliate ids default to the queryable 'none'
    // sentinel (firestore can't query null), so a dropped param must read as absent here
    static isRealAttributionValue(value) {
        return isStringNotEmpty(value) && toLower(value) !== 'none';
    }

    /*
        Session identity is decided by attribution alone (affiliateId/subAffiliateId); everything
        else is enrichment that rides along. A fork mints a new visit only when we switch between
        two separately-payable entities; attribution first appearing on an unclaimed session, or a
        dropped param, continues the existing visit. Non-attribution fields (click ids, offer tokens)
        never fork - that conflation is what minted a second, attribution-clobbered visit on the
        readyrx.com -> app.readyrx.com handoff.
    */
    static shouldForkVisit(currentVisit, newData) {
        const currentAffiliate = this.isRealAttributionValue(currentVisit?.affiliateId) ? toLower(currentVisit.affiliateId) : null;
        const newAffiliate = this.isRealAttributionValue(newData?.affiliateId) ? toLower(newData.affiliateId) : null;
        const currentSub = this.isRealAttributionValue(currentVisit?.subAffiliateId) ? toLower(currentVisit.subAffiliateId) : null;
        const newSub = this.isRealAttributionValue(newData?.subAffiliateId) ? toLower(newData.subAffiliateId) : null;

        // R1: a real affiliate switches to a different real affiliate
        if (currentAffiliate !== null && newAffiliate !== null && newAffiliate !== currentAffiliate) {
            return true;
        }

        // R2: same established affiliate (a dropped param keeps the stored value, so still established),
        // the publisher (sub) appears or switches to a different real value - a distinct payable party
        const isAffiliateEstablishedAndUnchanged = currentAffiliate !== null && (newAffiliate === null || newAffiliate === currentAffiliate);
        if (isAffiliateEstablishedAndUnchanged && newSub !== null && newSub !== currentSub) {
            return true;
        }

        // R3 (affiliate first appears on an unclaimed session) and R4 (dropped params / no change)
        // all continue the existing visit
        return false;
    }

    // fire-and-forget: the server merges newly-arrived tracking fields onto the continued visit per
    // the seed-when-empty / last-touch policy (one merge implementation shared with readyrx.js). never
    // awaited - enrichment must not add a round-trip to the funnel-critical initialize() path
    static enrichContinuedVisit(currentVisit, trackingData) {
        try {
            if (isStringEmpty(currentVisit?.id)) { return; }
            if (isStringEmpty(TrackingService.SITE_VISIT_UPDATE_API_URL)) { return; }

            fetch(TrackingService.SITE_VISIT_UPDATE_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...trackingData, id: currentVisit.id })
            }).catch(() => { /* best effort - enrichment is non-critical */ });
        } catch (e) {
            // never block init on enrichment
        }
    }

    static getPathParameters() {
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const [rootSegment, secondSegment, thirdSegment] = pathParts;

        if (['f', 'fc'].includes(rootSegment)) {
            return {
                programSlug: secondSegment ?? null,
                offerToken: thirdSegment ?? null,
                productSku: null,
                productCategorySlug: null
            };
        }

        if (rootSegment === 'shop' && secondSegment === 'c') {
            return {
                programSlug: null,
                offerToken: null,
                productSku: null,
                productCategorySlug: thirdSegment ?? null
            };
        }

        return {
            programSlug: null,
            offerToken: null,
            productSku: null,
            productCategorySlug: null
        };
    }

    static getCanonicalOfferToken(offerToken) {
        if (!isStringNotEmpty(offerToken)) {
            return null;
        }

        // must match readyrx.js getCanonicalOfferToken exactly - a divergent canonical form makes the
        // same landing offer read as different across the two trackers. Webflow uses "-gs" pages as
        // goal-survey landing variants, not separate offer groups
        const trimmed = toLower(offerToken.trim());
        const normalizedOfferToken = trimmed.endsWith('-gs') ? trimmed.slice(0, -3) : trimmed;
        const aliasMap = {
            bc: 'lb',
            fce90: 'fce',
            'face-cream-e-90': 'fce',
            fct90: 'fct',
            'face-cream-r-90': 'fct',
            'face-cream-t-90': 'fct'
        };

        return aliasMap[normalizedOfferToken] ?? normalizedOfferToken;
    }

    static async gatherTrackingData(params, pathParams, deviceInfo = {}) {
        const productIdentityData = {
            offerGroupId: params.get('offerGroupId') || params.get('offer_group_id') || params.get('og') || pathParams.offerGroupId || null,
            offerToken: params.get('offerToken') || params.get('offer_token') || params.get('offer') || pathParams.offerToken || null,
            productSku: params.get('productSku') || params.get('product_sku') || params.get('sku') || pathParams.productSku || null
        };

        // Standard tracking data (ipAddress may be null here; createsitevisitapi stamps the
        // caller's IP server-side when it is missing)
        const baseData = {
            visitorId: getCookie(TrackingService.VISITOR_ID_COOKIE) || crypto.randomUUID(),
            ipAddress: deviceInfo.ipAddress ?? null,
            userAgent: getClientUserAgent(),
            referrerUrl: getReferrerUrl(),
            deviceFingerprint: deviceInfo.deviceFingerprint || await this.initializeDeviceFingerprint(),
            url: window.location.href,
            pathName: window.location.pathname,
            initialPath: deviceInfo.initialPath || window.location.pathname,
            initialUrl: deviceInfo.initialUrl || window.location.href,
            testMode: params.get('test') === 'true'
        };

        // Affiliate & UTM parameters
        const affiliateData = {
            // Main affiliate params
            affiliateId: params.get('a') ? toLower(params.get('a')) : (params.get('fpr') ? toLower(params.get('fpr')) : 'none'),
            subAffiliateId: params.get('sa') ? toLower(params.get('sa')) : 'none',
            campaignSlug: params.get('c') ? toLower(params.get('c')) : null,
            creativeId: params.get('cr') ? toLower(params.get('cr')) : null,
            version: params.get('v') ? toLower(params.get('v')) : null,
            couponCode: params.get('coupon') ? (['alwaysready'].includes(toLower(params.get('coupon'))) ? 'alwaysready26' : toLower(params.get('coupon'))) : TrackingService.DEFAULT_COUPON_CODE,
            source: params.get('s') ? toLower(params.get('s')) : null,

            // Click IDs and tracking params
            clickId: this.getFirstValidClickId(params),
            fbclid: params.get('fbclid') ? params.get('fbclid') : null,
            ttclid: params.get('ttclid') ? params.get('ttclid') : null,
            gclid: params.get('gclid') ? params.get('gclid') : null,
            irclickid: params.get('irclickid') ? params.get('irclickid') : null,
            rdt_cid: params.get('rdt_cid') ? params.get('rdt_cid') : null,
            SsCid: params.get('ScCid') || params.get('SsCid') || null,
            msclkid: params.get('msclkid') ? params.get('msclkid') : null,
            twclid: params.get('twclid') ? params.get('twclid') : null,
            nbclid: params.get('newsbreak_cid') || params.get('nbclid') || null,
            gbraid: params.get('gbraid') ? params.get('gbraid') : null,

            // Everflow parameters
            offerId: params.get('oid') ? params.get('oid') : null,
            affId: params.get('affid') ? params.get('affid') : null,
            sub1: params.get('sub1') ? params.get('sub1') : null,
            sub2: params.get('sub2') ? params.get('sub2') : null,
            sub3: params.get('sub3') ? params.get('sub3') : null,
            sub4: params.get('sub4') ? params.get('sub4') : null,
            sub5: params.get('sub5') ? params.get('sub5') : null,
            uId: params.get('uid') ? params.get('uid') : null,
            sourceId: params.get('source_id') ? params.get('source_id') : null,
            transactionId: (params.get('_ef_transaction_id') || params.get('sid') || params.get('cid')) ?? null,
            sessionId: params.get('sid') ? params.get('sid') : null,
            theOfferSession: params.get('sid') ? params.get('sid') : null,

            // ecommerce params
            addon: params.get('addon') ? isBooleanTrue(params.get('addon')) : false,
            leadId: params.get('lid') ? params.get('lid') : null,

            // UTM parameters
            utm_source: params.get('utm_source') ?? null,
            utm_medium: params.get('utm_medium') ?? null,
            utm_campaign: params.get('utm_campaign') ?? null,
            utm_term: params.get('utm_term') ?? null,
            utm_content: params.get('utm_content') ?? null,

            // Product info from path
            ...this.normalizeProductInfo({
                ...pathParams,
                ...productIdentityData
            })
        };

        return {
            ...baseData,
            ...affiliateData,
            source: affiliateData.utm_source || affiliateData.affiliateId
        };
    }

    static getFirstValidClickId(params) {
        const clickIdParams = ['cid', 'irclickid', 'fbclid', 'ttclid', 'gclid', 'rdt_cid', 'ScCid', 'SsCid', 'msclkid', 'twclid', 'nbclid', 'gbraid', 'newsbreak_cid', '_ef_transaction_id'];
        for (const param of clickIdParams) {
            const value = params.get(param);
            if (value) return value;
        }

        return null;
    }

    static normalizeProductInfo(pathParams) {
        let { programSlug, productSku = null, offerGroupId = null, offerToken = null } = pathParams;

        return {
            programSlug,
            productSku: isStringNotEmpty(productSku) ? productSku : null,
            offerGroupId: isStringNotEmpty(offerGroupId) ? offerGroupId : null,
            offerToken: this.getCanonicalOfferToken(offerToken),
            productCategorySlug: pathParams.productCategorySlug
        };
    }

    static async processAffiliateData(trackingData, siteVisitId = null) {
        let affiliateInfo = new AffiliateInfo({
            ...trackingData,
            siteVisitId,
            transactionId: trackingData.transactionId || trackingData.theOfferSession
        }).toDto();

        // Handle Facebook click ID special case
        if (isStringNotEmpty(trackingData.fbclid)) {
            const siteVisitAt = Math.floor(Date.now() / 1000);
            const fbc = `fb.1.${siteVisitAt}.${trackingData.fbclid}`;
            setCookie(TrackingService.FBC_COOKIE, fbc, 90);
            affiliateInfo.fbc = fbc;
            affiliateInfo.siteVisitAt = siteVisitAt;
        }

        return affiliateInfo;
    }

    static async initializeDeviceFingerprint() {
        return new Promise((resolve) => {
            let settled = false;
            let timeoutId = null;

            const isLocalHost = () => {
                const hostname = window.location.hostname;
                return ['localhost', '127.0.0.1', '', '::1'].includes(hostname);
            }

            if (isLocalHost()) {
                resolve(null);
                return;
            }

            const finish = (value) => {
                if (settled) return;
                settled = true;
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                resolve(value ?? null);
            };

            if (!DeviceFingerprint || typeof DeviceFingerprint.initializeScriptAsync !== 'function') {
                logger.logDebugEvent('TRACKING_DEVICE_FINGERPRINT', 'Device fingerprint library unavailable.', getTestModeConsole());
                finish(null);
                return;
            }

            timeoutId = setTimeout(() => {
                logger.logDebugEvent('TRACKING_DEVICE_FINGERPRINT', 'Device fingerprint initialization timed out.', getTestModeConsole());
                finish(null);
            }, 5000);

            const handleFailure = (error) => {
                if (error) {
                    logger.logErrorEvent('TRACKING_DEVICE_FINGERPRINT', error, 'Device fingerprint initialization failed', getTestModeConsole());
                }
                finish(null);
            };

            const handleSuccess = () => {
                try {
                    DeviceFingerprint.AfterResult((result) => {
                        finish(result?.device_id ?? null);
                    });
                    DeviceFingerprint.AfterFailure(handleFailure);
                    DeviceFingerprint.Init();
                } catch (callbackError) {
                    handleFailure(callbackError);
                }
            };

            try {
                const scriptInit = DeviceFingerprint.initializeScriptAsync(IP_QUALITY_SCORE_FINGERPRINT_TRACKER_KEY);

                if (scriptInit && typeof scriptInit.then === 'function') {
                    scriptInit.then(handleSuccess).catch(handleFailure);
                } else {
                    handleSuccess();
                }
            } catch (error) {
                handleFailure(error);
            }
        });
    }

    static async createSiteVisit(trackingData) {
        try {
            // Handle Facebook click ID special case
            if (isStringNotEmpty(trackingData.fbclid)) {
                const siteVisitAt = Math.floor(Date.now() / 1000);
                const fbc = `fb.1.${siteVisitAt}.${trackingData.fbclid}`;
                setCookie(TrackingService.FBC_COOKIE, fbc, 90); // 90 days
                trackingData.fbc = fbc;
                trackingData.siteVisitAt = siteVisitAt;
            }

            // fresh client-generated id makes creation idempotent across the API call and the
            // Firestore fallback; never inherit one - re-attribution passes the previous visit's
            // data in, and its id would target the old visit instead of creating a new one
            const visitId = crypto.randomUUID();
            trackingData.id = visitId;

            // create through the API so the server stamps the caller's IP; fall back to a direct
            // Firestore write if the API is unreachable or hangs so visits are never lost
            let newSiteVisit = null;
            if (isStringNotEmpty(TrackingService.SITE_VISIT_API_URL)) {
                try {
                    const abortController = new AbortController();
                    const abortTimer = setTimeout(() => abortController.abort(), 5000);
                    try {
                        const apiResponse = await fetch(TrackingService.SITE_VISIT_API_URL, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(trackingData),
                            signal: abortController.signal
                        });

                        if (apiResponse.ok) {
                            // the body read shares the abort signal, so a stalled body cannot hang init
                            newSiteVisit = await apiResponse.json();
                        } else {
                            logger.logErrorEvent('TRACKING_CREATE_VISIT', new Error(`createsitevisitapi responded with status ${apiResponse.status}`), 'createsitevisitapi returned a non-OK status', getTestModeConsole());
                        }
                    } finally {
                        clearTimeout(abortTimer);
                    }
                } catch (apiError) {
                    logger.logErrorEvent('TRACKING_CREATE_VISIT', apiError, 'Error calling createsitevisitapi', getTestModeConsole());
                }
            }

            if (isObjectEmpty(newSiteVisit) || isStringEmpty(newSiteVisit?.id)) {
                // the fallback bypasses the server's IP stamping, so try to resolve one client-side
                if (isStringEmpty(trackingData.ipAddress)) {
                    try {
                        trackingData.ipAddress = await getClientIPAddress();
                    } catch (ipError) {
                        // keep null - better a visit without an IP than no visit
                    }
                }

                newSiteVisit = await this.boundInitAwait(new SiteVisit({ ...trackingData }).createWithId(visitId));
            }

            if (newSiteVisit) {
                // Store full tracking data in cookie
                const fullVisitData = {
                    ...trackingData,
                    ...newSiteVisit,
                    id: newSiteVisit.id,
                    // a new visit starts with a clean flag - storeVisitData merges over the
                    // previous visit's data, and inheriting true suppresses re-attributed impressions
                    impressionTracked: false
                };

                await this.storeVisitData(fullVisitData);
                // the stored visit reflects current attribution again
                this.isStoredVisitStale = false;

                // the only place the landing pageview is produced (trackPageView skips the
                // landing path), emitted here so both create paths produce the same event set
                await this.boundInitAwait(this.trackLandingPageView(fullVisitData));

                return fullVisitData;
            }

            return null;
        } catch (error) {
            logger.logErrorEvent('TRACKING_CREATE_VISIT', error, 'Error creating site visit', getTestModeConsole());
            return null;
        }
    }

    // single definition of the pageview event shape so the landing and in-session variants
    // can't drift apart
    static async emitPageView(siteVisit, pathName, url) {
        await new SiteEvent({
            ...siteVisit,
            funnel: 'Page View',
            stepNumber: 0,
            stepName: 'Page View',
            direction: 'Viewed',
            eventName: 'pageview',
            pathName,
            url,
            siteVisitId: siteVisit.id
        }).create();
    }

    static async trackLandingPageView(siteVisit) {
        try {
            if (!siteVisit) return;

            // stamp the location captured at init start - an SPA navigation during init would
            // otherwise record the wrong page, and the url's query string must survive for
            // ad-platform event_source_url and the Everflow transaction-id fallback
            const landingPath = isStringNotEmpty(siteVisit.initialPath) ? siteVisit.initialPath : window?.location?.pathname;
            const landingUrl = isStringNotEmpty(siteVisit.initialUrl) ? siteVisit.initialUrl : window?.location?.href;

            await this.emitPageView(siteVisit, landingPath, landingUrl);
        } catch (error) {
            logger.logErrorEvent('TRACKING_LANDING_PAGEVIEW', error, 'Error tracking landing page view', getTestModeConsole());
        }
    }

    static async trackImpression(siteVisit) {
        try {
            if (!siteVisit) return;

            // Check if impression already tracked for this visit
            const visitData = await this.getStoredVisitData();
            if (visitData?.impressionTracked) return;

            // Create impression event
            await new SiteEvent({
                ...siteVisit,
                funnel: 'Impression',
                stepNumber: 0,
                stepName: 'Impression',
                direction: 'Impression',
                eventName: 'impression',
                pathName: window?.location?.pathname,
                url: window?.location?.href,
                siteVisitId: siteVisit.id
            }).create();

            // Update stored visit data with impression tracked
            await this.storeVisitData({
                ...siteVisit,
                impressionTracked: true
            });
            logger.logEvent('TRACKING_IMPRESSION', `Impression tracked: ${siteVisit.id}`, getTestModeConsole());
        } catch (error) {
            logger.logErrorEvent('TRACKING_IMPRESSION', error, 'Error tracking impression', getTestModeConsole());
        }
    }

    static async trackPageView(siteVisit) {
        try {
            if (!siteVisit) return;

            const currentPath = window.location.pathname;
            if (currentPath === siteVisit.initialPath) return;

            await this.emitPageView(siteVisit, currentPath, window.location.href);
            logger.logEvent('TRACKING_PAGEVIEW', `Page view tracked: ${currentPath}`, getTestModeConsole());
        } catch (error) {
            logger.logErrorEvent('TRACKING_PAGEVIEW', error, 'Error tracking page view', getTestModeConsole());
        }
    }

    static async trackEvent(eventName, eventData = {}, userData = {}) {
        try {
            const visitData = await this.getStoredVisitData();
            if (!visitData) return;

            await new SiteEvent({
                ...visitData,
                funnel: eventData.funnel || 'Custom Event',
                stepNumber: eventData.stepNumber || 0,
                stepName: eventName,
                direction: eventData.direction || 'Custom',
                eventName: eventName,
                pathName: window.location.pathname,
                url: window.location.href,
                siteVisitId: visitData.id,
                ...eventData,
                ...userData
            }).create();
            logger.logEvent('TRACKING_EVENT', `Event tracked: ${eventName}`, getTestModeConsole());
        } catch (error) {
            logger.logErrorEvent('TRACKING_EVENT', error, 'Error tracking event', getTestModeConsole());
        }
    }

    static resetVisit() {
        const domain = getDomain();
        document.cookie = `${TrackingService.VISIT_ID_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${domain};SameSite=Lax`;
        // document.cookie = `${TrackingService.FBC_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;domain=${domain};SameSite=Lax`;
    }
}

export default TrackingService;
